import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Sparkle, ArrowUpRight } from "@phosphor-icons/react";
import { CONTINUOUS_DYNAMICS } from "./continuousDynamics";

const DYNAMIC_TO_LINK = { connections: "connections", leadership: "leadership" };

function RecentReflectionsStrip({ dynamicKey }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/dynamics/${dynamicKey}/reflections?limit=5`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [dynamicKey]);
  if (items === null) return null;
  if (items.length === 0) {
    return (
      <div className="border-t border-border pt-5">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-2">Recent reflections captured under this lens</div>
        <p className="text-[0.75rem] italic text-muted-foreground" style={{ fontFamily: "Georgia, serif" }}>
          Nothing captured under this lens yet.
        </p>
      </div>
    );
  }
  return (
    <div className="border-t border-border pt-5" data-testid={`dynamic-recent-strip-${dynamicKey}`}>
      <div className="text-[0.625rem] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-2">Recent reflections captured under this lens</div>
      <ul className="space-y-2">
        {items.map((it) => {
          const to = it.kind === "reflection" && it.engagement_id
            ? `/engagements/${it.engagement_id}`
            : it.kind === "note"
              ? `/notes#note-${it.id}`
              : "#";
          const label = it.kind === "reflection"
            ? (it.prompt || "Engagement reflection")
            : (it.title || "Note");
          return (
            <li key={`${it.kind}-${it.id}`} data-testid={`dynamic-recent-item-${it.kind}-${it.id}`}>
              <Link to={to} className="block group">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-sm truncate" style={{ fontFamily: "Georgia, serif" }}>
                    <span className="text-[0.5938rem] font-mono uppercase tracking-widest text-brass mr-2 not-italic">{it.kind === "reflection" ? "reflection" : "note"}</span>
                    {label}
                  </div>
                  <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">
                    {it.author_name || ""}{it.created_at ? ` · ${new Date(it.created_at).toLocaleDateString()}` : ""}
                  </div>
                </div>
                {it.excerpt && <div className="text-[0.75rem] text-muted-foreground mt-0.5 truncate">{it.excerpt}</div>}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Pinned explainer card that renders below the Embrace arcs when an advisor
 * clicks one of the two continuous dynamic labels (Connections &
 * Relationships or Leadership & Storytelling).
 *
 * Governance rules encoded here:
 *   • Card content is CANONICAL Yadid teaching material — no per-user
 *     editing (see /components/method/continuousDynamics.js).
 *   • The card is visually distinct from the four movement drawers: it is
 *     inline (not overlay), outlined (not shadowed), and topped with a
 *     brass hairline to signal "continuous condition, not a phase."
 *   • Esc + click-outside dismiss are handled by the parent; this
 *     component only owns its own close button + focus-on-mount.
 */
export default function ContinuousDynamicCard({ dynamicKey, onClose }) {
  const dynamic = CONTINUOUS_DYNAMICS[dynamicKey];
  const headingRef = useRef(null);
  const navigate = useNavigate();

  // Move focus into the card once it pins so a keyboard-driven advisor lands
  // on the heading and can Tab into the content or Esc back out.
  useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, [dynamicKey]);

  if (!dynamic) return null;

  const captureInBriefingStudio = () => {
    // Subtle jump-off — opens Briefing Studio with a query hint so the
    // advisor can pick the briefing type (client / engagement / follow-up)
    // and start recording or writing right away. The hint is a soft seed,
    // not a hard prefill: the Briefing Studio can read it if desired but
    // never mutates canonical records based on it.
    navigate(`/notes?dynamic=${encodeURIComponent(dynamic.key)}`);
  };

  return (
    <div
      className="border border-border bg-card mt-6 animate-fade-up"
      data-testid={`continuous-dynamic-card-${dynamic.key}`}
      role="region"
      aria-labelledby={`dynamic-heading-${dynamic.key}`}
    >
      {/* Brass hairline on top edge — signals "continuous condition" without
          promoting the card to phase-level visual weight. */}
      <div className="h-px bg-brass/70" aria-hidden />

      <div className="p-6 md:p-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-brass flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" />
              Continuous dynamic · {dynamic.shortLabel}
            </div>
            <h2
              id={`dynamic-heading-${dynamic.key}`}
              ref={headingRef}
              tabIndex={-1}
              className="mt-3 max-w-3xl outline-none focus:ring-2 focus:ring-brass/40"
              style={{ fontFamily: "Georgia, serif", fontSize: "1.5rem", lineHeight: 1.3, fontStyle: "italic" }}
            >
              {dynamic.framingQuestion}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close continuous dynamic explainer"
            data-testid={`continuous-dynamic-close-${dynamic.key}`}
            className="text-muted-foreground hover:text-foreground shrink-0 mt-1"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground max-w-3xl">
          {dynamic.explainer}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 border-t border-border pt-6">
          {dynamic.groupings.map((g) => (
            <div key={g.key} data-testid={`dynamic-grouping-${dynamic.key}-${g.key}`}>
              <div className="text-[0.625rem] font-mono uppercase tracking-[0.22em] text-primary">
                {g.label}
              </div>
              <ul className="mt-3 space-y-2">
                {g.prompts.map((p, i) => (
                  <li key={i} className="text-sm text-foreground leading-relaxed pl-3 border-l border-primary/30">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-5 flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-[0.625rem] font-mono uppercase tracking-[0.22em] text-muted-foreground">
              Working notes
            </div>
            <p
              className="mt-2 text-sm leading-relaxed"
              style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}
            >
              {dynamic.workingNotesPrompt}
            </p>
          </div>
          <button
            type="button"
            onClick={captureInBriefingStudio}
            data-testid={`continuous-dynamic-capture-${dynamic.key}`}
            className="text-[0.6875rem] font-mono uppercase tracking-widest text-primary hover:text-brass border-b border-transparent hover:border-brass transition-colors shrink-0"
          >
            Capture in Advisory Desk →
          </button>
        </div>

        <RecentReflectionsStrip dynamicKey={dynamic.key} />
      </div>
    </div>
  );
}
